import React, { useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useSession } from '@/lib/SessionContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  ShieldCheck, Users, Building2, Database, ArrowRight, Sparkles, Activity
} from 'lucide-react';
import AsaAssistant from '@/components/shared/AsaAssistant';

export default function WorkspaceDashboard() {
  const navigate = useNavigate();
  const { member: currentMember } = useSession();

  // Guard access to developer / owner only is handled by AppLayout / Portal Access Control

  // Fetch Clients
  const { data: clients = [] } = useQuery({
    queryKey: ["client-accounts"],
    queryFn: () => base44.entities.ClientAccount.list("-created_date"),
  });

  // Fetch Team Members
  const { data: teamMembers = [] } = useQuery({
    queryKey: ["team-members"],
    queryFn: () => base44.entities.TeamMember.list("-join_date"),
  });

  const verifiedCount = clients.filter(c => c.status === 'verified').length;
  const unverifiedCount = clients.filter(c => c.status !== 'verified').length;

  const asaInsights = useMemo(() => {
    const insights = [];
    if (unverifiedCount > 0) {
      insights.push({
        type: 'warning',
        text: `Ada **${unverifiedCount} pendaftaran Klien baru** yang belum diverifikasi. Cek halaman Database Klien sekarang buat nge-acc mereka ya.`
      });
    } else {
      insights.push({
        type: 'info',
        text: `Semua Klien sudah tervalidasi. Dashboard workspace aman terkendali!`
      });
    }
    return insights;
  }, [unverifiedCount]);

  return (
    <div className="space-y-8 pb-16 text-[#302621]">
      
      {/* 1. Header Premium */}
      <div className="relative rounded-3xl overflow-hidden bg-gradient-to-r from-[#302621] to-[#4A3B32] p-8 text-white border border-[#E6D5B8]/20 shadow-xl">
        <div className="absolute top-0 right-0 p-8 opacity-10">
          <ShieldCheck className="w-48 h-48 text-[#D4AF37]" />
        </div>
        <div className="relative z-10 space-y-4">
          <span className="bg-[#D4AF37]/25 text-[#D4AF37] border border-[#D4AF37]/40 px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase">
            WORKSPACE ADMIN HUB
          </span>
          <div>
            <h1 className="text-3xl font-black tracking-tight leading-none text-[#FFFDF9]">Satala Workspace</h1>
            <p className="text-xs text-[#E6D5B8] font-medium mt-2 leading-relaxed max-w-xl">
              Selamat datang di pusat kemudi utama pabrik Satala. Kendalikan hak akses tim, kelola verifikasi mitra resmi, dan atur identitas perusahaan dari satu dashboard terpadu.
            </p>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-[#E6D5B8]/15">
            <div className="bg-[#FFFDF9]/5 border border-[#FFFDF9]/10 rounded-xl p-3">
              <span className="text-[10px] font-bold text-[#E6D5B8] uppercase">Mitra Pabrik</span>
              <p className="text-xl font-black mt-1 text-[#FFFDF9]">{verifiedCount} Brand</p>
            </div>
            <div className="bg-[#FFFDF9]/5 border border-[#FFFDF9]/10 rounded-xl p-3">
              <span className="text-[10px] font-bold text-[#E6D5B8] uppercase">Antrean Verifikasi</span>
              <p className="text-xl font-black mt-1 text-[#D4AF37]">{unverifiedCount} Klien</p>
            </div>
            <div className="bg-[#FFFDF9]/5 border border-[#FFFDF9]/10 rounded-xl p-3">
              <span className="text-[10px] font-bold text-[#E6D5B8] uppercase">Total Staff</span>
              <p className="text-xl font-black mt-1 text-[#FFFDF9]">{teamMembers.length} Personel</p>
            </div>
            <div className="bg-[#FFFDF9]/5 border border-[#FFFDF9]/10 rounded-xl p-3">
              <span className="text-[10px] font-bold text-[#E6D5B8] uppercase">Status Keamanan</span>
              <p className="text-xl font-black mt-1 text-emerald-400">Enkripsi Aktif</p>
            </div>
          </div>
        </div>
      </div>

      <AsaAssistant 
        insights={asaInsights}
        title="Asa Admin Assistant"
      />

      {/* 2. Grid Aksi Cepat / Menu Shortcuts */}
      <div className="space-y-4">
        <h2 className="text-lg font-black tracking-tight text-[#302621]">Aksi Cepat & Navigasi</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          
          <Card className="border-[#E6D5B8]/50 shadow-xs hover:shadow-md transition-shadow bg-white rounded-2xl">
            <CardContent className="p-6 space-y-4">
              <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-200/50 flex items-center justify-center text-[#8C6208]">
                <Database className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-sm">Database Klien Pabrik</h3>
                <p className="text-xs text-slate-400 mt-1">Verifikasi pendaftaran mitra brand, ubah limit kredit PO, dan sinkronkan database repeat order.</p>
              </div>
              <Link to="/office/workspace/clients">
                <Button className="w-full bg-[#302621] text-white hover:bg-[#4A3B32] text-xs font-bold gap-1 rounded-xl mt-2 h-9">
                  Buka Database Klien <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="border-[#E6D5B8]/50 shadow-xs hover:shadow-md transition-shadow bg-white rounded-2xl">
            <CardContent className="p-6 space-y-4">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-200/50 flex items-center justify-center text-indigo-700">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-sm">Manajemen Tim & Akses</h3>
                <p className="text-xs text-slate-400 mt-1">Tambahkan anggota tim baru, atur konfigurasi hak akses halaman spesifik, dan kelola QR code login.</p>
              </div>
              <Link to="/office/workspace/team">
                <Button className="w-full bg-[#302621] text-white hover:bg-[#4A3B32] text-xs font-bold gap-1 rounded-xl mt-2 h-9">
                  Kelola Tim & Akses <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="border-[#E6D5B8]/50 shadow-xs hover:shadow-md transition-shadow bg-white rounded-2xl">
            <CardContent className="p-6 space-y-4">
              <div className="w-10 h-10 rounded-xl bg-orange-50 border border-orange-200/50 flex items-center justify-center text-orange-700">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-sm">Absensi Karyawan</h3>
                <p className="text-xs text-slate-400 mt-1">Pantau log kehadiran dan sinkronisasi data dari mesin fingerprint (ADMS) secara terpusat.</p>
              </div>
              <Link to="/office/workspace/absensi">
                <Button className="w-full bg-[#302621] text-white hover:bg-[#4A3B32] text-xs font-bold gap-1 rounded-xl mt-2 h-9">
                  Cek Absensi <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="border-[#E6D5B8]/50 shadow-xs hover:shadow-md transition-shadow bg-white rounded-2xl">
            <CardContent className="p-6 space-y-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200/50 flex items-center justify-center text-emerald-700">
                <Building2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-sm">Identitas Perusahaan</h3>
                <p className="text-xs text-slate-400 mt-1">Atur profil resmi pabrik, tanda tangan direktur, kota pembuatan invoice, serta aset kop surat resmi.</p>
              </div>
              <Link to="/office/workspace/settings">
                <Button className="w-full bg-[#302621] text-white hover:bg-[#4A3B32] text-xs font-bold gap-1 rounded-xl mt-2 h-9">
                  Atur Identitas Pabrik <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="border-[#E6D5B8]/50 shadow-xs hover:shadow-md transition-shadow bg-white rounded-2xl">
            <CardContent className="p-6 space-y-4">
              <div className="w-10 h-10 rounded-xl bg-rose-50 border border-rose-200/50 flex items-center justify-center text-rose-700">
                <Activity className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-sm">Healthy Web</h3>
                <p className="text-xs text-slate-400 mt-1">Pantau statistik penyimpanan VPS, kecepatan database, dan laporan analisis ASA secara real-time.</p>
              </div>
              <Link to="/office/workspace/health">
                <Button className="w-full bg-[#302621] text-white hover:bg-[#4A3B32] text-xs font-bold gap-1 rounded-xl mt-2 h-9">
                  Pantau Kesehatan <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="border-[#E6D5B8]/50 shadow-xs hover:shadow-md transition-shadow bg-white rounded-2xl">
            <CardContent className="p-6 space-y-4">
              <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-200/50 flex items-center justify-center text-blue-700">
                <Database className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-sm">Backup & Restore</h3>
                <p className="text-xs text-slate-400 mt-1">Ekspor seluruh data ke format Excel (.xlsx) untuk cadangan, atau impor untuk memulihkan data.</p>
              </div>
              <Link to="/office/workspace/backup">
                <Button className="w-full bg-[#302621] text-white hover:bg-[#4A3B32] text-xs font-bold gap-1 rounded-xl mt-2 h-9">
                  Buka Pusat Backup <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </Link>
            </CardContent>
          </Card>

        </div>
      </div>

      {/* 3. Panel Sekunder & Antrean Sistem */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Klien Terbaru Butuh Verifikasi */}
        <Card className="border-[#E6D5B8]/40 shadow-xs bg-white rounded-2xl lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#8C6208]" />
              Pendaftar Klien Terbaru (Menunggu Validasi)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {clients.filter(c => c.status !== 'verified').length === 0 ? (
              <div className="text-center py-8 text-xs text-slate-400 font-medium">
                Tidak ada pendaftaran klien baru yang menggantung. Semua mitra terverifikasi.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {clients.filter(c => c.status !== 'verified').slice(0, 5).map(c => (
                  <div key={c.id} className="py-3 flex items-center justify-between gap-4 text-xs">
                    <div>
                      <p className="font-extrabold text-slate-800">{c.brand_name || 'Brand Baru'}</p>
                      <p className="text-[10px] text-[#7A695B] font-medium">{c.company_name} · PIC: {c.pic}</p>
                    </div>
                    <Link to="/office/workspace/clients">
                      <Button variant="ghost" size="sm" className="h-7 text-[10px] text-[#8C6208] hover:bg-[#F9F5EC] font-bold">
                        Validasi Klien
                      </Button>
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Daftar Staff Terbaru */}
        <Card className="border-[#E6D5B8]/40 shadow-xs bg-white rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-bold flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-600" />
              Personel Tim Terbaru
            </CardTitle>
          </CardHeader>
          <CardContent>
            {teamMembers.length === 0 ? (
              <div className="text-center py-8 text-xs text-slate-400 font-medium">
                Belum ada anggota tim terdaftar.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {teamMembers.slice(0, 4).map(member => (
                  <div key={member.id} className="py-3 flex items-center justify-between gap-4 text-xs">
                    <div>
                      <p className="font-extrabold text-slate-800">{member.full_name || member.username}</p>
                      <p className="text-[10px] text-[#7A695B] font-medium uppercase">{member.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

      </div>

    </div>
  );
}
